import { useState } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  LinkIcon,
  EyeIcon,
  EnvelopeIcon,
  CheckBadgeIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useAuth, ROLE_RECRUITMENT_ADMIN, ROLE_SI_ADMIN, ROLE_SI_RH } from '@/contexts/AuthContext';
import { ConfirmDialog } from '@/components/shared';
import { useCandidatosQuery, useCandidatoMutations } from '../../hooks/data';
import type { Candidato } from '../../models';
import { STATUS_CANDIDATO_LABELS, STATUS_CANDIDATO_BADGE_CLASS } from './candidatosListPage.definitions';
import AdicionarCandidatoModal from './components/AdicionarCandidatoModal';
import EditarCandidatoModal from './components/EditarCandidatoModal';
import VisualizarCandidatoModal from './components/VisualizarCandidatoModal';

type AcaoConfirmacao =
  | { tipo: 'excluir'; candidato: Candidato }
  | { tipo: 'enviarLink'; candidato: Candidato }
  | { tipo: 'enviarLinkEtapa2'; candidato: Candidato };

const TITULO_CONFIRMACAO: Record<AcaoConfirmacao['tipo'], string> = {
  excluir: 'Excluir candidato',
  enviarLink: 'Enviar link por e-mail',
  enviarLinkEtapa2: 'Enviar link da Etapa 2',
};

function mensagemConfirmacao(acao: AcaoConfirmacao): string {
  switch (acao.tipo) {
    case 'excluir':
      return `Excluir o candidato "${acao.candidato.nome}"? Essa ação não pode ser desfeita.`;
    case 'enviarLink':
      return `Enviar e-mail com o link do formulário para ${acao.candidato.nome} (${acao.candidato.email})?`;
    case 'enviarLinkEtapa2':
      return `Enviar e-mail com o link da Etapa 2 para ${acao.candidato.nome} (${acao.candidato.email})?`;
  }
}

/** Tooltip dos ícones de Copiar/Enviar — a ação ("Enviar e-mail da Etapa 1") enquanto
 * nunca foi usado; depois do primeiro uso, o que aconteceu ("E-mail da Etapa 1 enviado em
 * 23/09/2026 às 14:30 por Fulano") — ver linkEnviadoEm/linkCopiadoEm (e equivalentes de
 * Etapa 2) em candidato.types.ts. Autor só aparece em registros gravados depois que a
 * function passou a salvar quem clicou (usos anteriores ficam só com data/hora). */
function tituloAcaoLink(
  rotuloAcao: string,
  rotuloFeito: string,
  usadoEm: { toDate: () => Date } | null | undefined,
  usadoPor: { name: string } | null | undefined
): string {
  if (!usadoEm) return rotuloAcao;
  const data = usadoEm.toDate();
  const dia = data.toLocaleDateString('pt-BR');
  const hora = data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const autor = usadoPor?.name ? ` por ${usadoPor.name}` : '';
  return `${rotuloFeito} em ${dia} às ${hora}${autor}`;
}

/**
 * Painel do módulo de Recrutamento — primeiro corte: só Candidatos (Vagas/Propostas
 * ficam pra depois, decisão explícita). "Adicionar Candidato" só cadastra — o envio do
 * link fica a critério do recrutador depois (copiar, ou mandar por e-mail), já que nem
 * todo candidato tem facilidade de acessar e-mail (decisão registrada: link direto tem
 * mais adesão). A lista reflete o status assim que o candidato responde (ver
 * submitCandidatoExterno, que grava direto no mesmo documento).
 */
export default function CandidatosListPage() {
  const { userHasRole, loading: autenticando } = useAuth();
  const isRecrutamentoAdmin = userHasRole(ROLE_RECRUITMENT_ADMIN);
  // Nome e status: todo mundo com role (gestores acompanham o andamento). Administrar SI e
  // Chamados DP (si_rh) também VEEM e-mail e respostas (olho) — pela
  // logística dos chamados de admissão eles precisam desses dados. Ações (adicionar, links,
  // decisão da proposta, editar, excluir) continuam só com o recrutador.
  const podeVerDados = isRecrutamentoAdmin || userHasRole(ROLE_SI_ADMIN) || userHasRole(ROLE_SI_RH);
  const { data: candidatos = [], isLoading: carregandoCandidatos, isError, error } = useCandidatosQuery();
  // Enquanto a autenticação ainda carrega, useCandidatosQuery fica "enabled: false" (ver
  // hook) — sem isso, isLoading cairia pra false nesse meio-tempo e piscaria "Nenhum
  // candidato cadastrado" antes da lista de verdade chegar.
  const isLoading = autenticando || carregandoCandidatos;
  const { enviarLink, copiarLink, excluirCandidato, marcarDecisaoProposta, enviarLinkEtapa2, copiarLinkEtapa2 } =
    useCandidatoMutations();
  const [modalAberto, setModalAberto] = useState(false);
  const [candidatoEditando, setCandidatoEditando] = useState<Candidato | null>(null);
  const [candidatoVisualizando, setCandidatoVisualizando] = useState<Candidato | null>(null);
  // Confirmação (ConfirmDialog) pras 3 ações que precisam de "tem certeza?": excluir e os
  // 2 botões que realmente mandam e-mail pro candidato — pedido do usuário depois de um
  // clique acidental (ícone de e-mail fica adjacente a outros que mudam de posição
  // conforme o status, facilitando escorregão).
  const [confirmacao, setConfirmacao] = useState<AcaoConfirmacao | null>(null);

  const handleExcluir = (candidato: Candidato) => setConfirmacao({ tipo: 'excluir', candidato });
  const handleEnviarLink = (candidato: Candidato) => setConfirmacao({ tipo: 'enviarLink', candidato });
  const handleEnviarLinkEtapa2 = (candidato: Candidato) => setConfirmacao({ tipo: 'enviarLinkEtapa2', candidato });

  const handleConfirmar = () => {
    if (!confirmacao) return;
    const { tipo, candidato } = confirmacao;
    if (tipo === 'excluir') excluirCandidato.mutate(candidato.id);
    if (tipo === 'enviarLink') enviarLink.mutate(candidato.id);
    if (tipo === 'enviarLinkEtapa2') enviarLinkEtapa2.mutate(candidato.id);
    setConfirmacao(null);
  };

  // Alterna a decisão da proposta — clicar no ícone já ativo desfaz (volta pra
  // "Preenchido"); clicar no outro troca direto (sem precisar desfazer primeiro).
  const handleAlternarDecisao = (candidato: Candidato, decisao: 'proposta_aceita' | 'proposta_recusada') => {
    const novoStatus = candidato.status === decisao ? 'preenchido' : decisao;
    marcarDecisaoProposta.mutate({ candidatoId: candidato.id, status: novoStatus });
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-montserrat text-3xl font-bold text-gray-900">Recrutamento</h1>
          <p className="mt-1 text-sm text-gray-600">Candidatos em processo seletivo.</p>
        </div>
        {isRecrutamentoAdmin && (
          <button type="button" onClick={() => setModalAberto(true)} className="btn-primary flex items-center gap-2">
            <PlusIcon className="h-5 w-5" />
            Adicionar Candidato
          </button>
        )}
      </div>

      <div className="space-y-2">
        {isLoading && <p className="text-sm text-gray-500">Carregando...</p>}
        {isError && (
          <p className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            Erro ao carregar candidatos: {(error as any)?.message || 'erro desconhecido'}
          </p>
        )}
        {!isLoading && !isError && candidatos.length === 0 && (
          <p className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
            Nenhum candidato cadastrado ainda.
          </p>
        )}
        {candidatos.map((candidato) => (
          <div
            key={candidato.id}
            className="flex items-center justify-between rounded-md border border-gray-200 bg-white px-4 py-3"
          >
            <div>
              <p className="font-montserrat font-medium text-gray-900">{candidato.nome}</p>
              {podeVerDados && <p className="text-sm text-gray-500">{candidato.email}</p>}
            </div>
            <div className="flex items-center gap-3">
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_CANDIDATO_BADGE_CLASS[candidato.status]}`}
              >
                {STATUS_CANDIDATO_LABELS[candidato.status]}
              </span>
              {podeVerDados && (
                <>
                  {isRecrutamentoAdmin && candidato.respostas == null && (
                    <>
                      <button
                        type="button"
                        className={
                          candidato.linkCopiadoEm
                            ? 'text-blue-500 hover:text-blue-600'
                            : 'text-gray-400 hover:text-gray-600'
                        }
                        onClick={() => copiarLink.mutate(candidato.id)}
                        disabled={copiarLink.isPending}
                        title={tituloAcaoLink(
                          'Copiar Link da Etapa 1',
                          'Link da Etapa 1 copiado',
                          candidato.linkCopiadoEm,
                          candidato.linkCopiadoPor
                        )}
                      >
                        <LinkIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        className={
                          candidato.linkEnviadoEm
                            ? 'text-blue-500 hover:text-blue-600'
                            : 'text-gray-400 hover:text-gray-600'
                        }
                        onClick={() => handleEnviarLink(candidato)}
                        disabled={enviarLink.isPending}
                        title={tituloAcaoLink(
                          'Enviar e-mail da Etapa 1',
                          'E-mail da Etapa 1 enviado',
                          candidato.linkEnviadoEm,
                          candidato.linkEnviadoPor
                        )}
                      >
                        <EnvelopeIcon className="h-4 w-4" />
                      </button>
                    </>
                  )}
                  {(candidato.respostas != null || candidato.respostasEtapa2 != null) && (
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-600"
                      onClick={() => setCandidatoVisualizando(candidato)}
                      title="Ver respostas"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                  )}
                  {isRecrutamentoAdmin &&
                    (candidato.status === 'preenchido' ||
                    candidato.status === 'proposta_aceita' ||
                    candidato.status === 'proposta_recusada') && (
                    <div
                      className="relative flex items-center gap-1 rounded-full border border-gray-200 px-1.5 py-0.5"
                      title="Decisão da proposta"
                    >
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] leading-none text-gray-400">
                        Proposta
                      </span>
                      <button
                        type="button"
                        className={
                          candidato.status === 'proposta_aceita'
                            ? 'text-green-600'
                            : 'text-gray-300 hover:text-green-600'
                        }
                        onClick={() => handleAlternarDecisao(candidato, 'proposta_aceita')}
                        disabled={marcarDecisaoProposta.isPending}
                        title={
                          candidato.status === 'proposta_aceita'
                            ? 'Desfazer — voltar para Preenchido'
                            : 'Marcar proposta aceita'
                        }
                      >
                        <CheckBadgeIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        className={
                          candidato.status === 'proposta_recusada' ? 'text-red-600' : 'text-gray-300 hover:text-red-600'
                        }
                        onClick={() => handleAlternarDecisao(candidato, 'proposta_recusada')}
                        disabled={marcarDecisaoProposta.isPending}
                        title={
                          candidato.status === 'proposta_recusada'
                            ? 'Desfazer — voltar para Preenchido'
                            : 'Marcar proposta recusada'
                        }
                      >
                        <XCircleIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  {isRecrutamentoAdmin &&
                    (candidato.status === 'proposta_aceita' || candidato.status === 'aguardando_preenchimento_etapa2') && (
                    <>
                      <button
                        type="button"
                        className={
                          candidato.linkEtapa2CopiadoEm
                            ? 'text-blue-500 hover:text-blue-600'
                            : 'text-gray-400 hover:text-gray-600'
                        }
                        onClick={() => copiarLinkEtapa2.mutate(candidato.id)}
                        disabled={copiarLinkEtapa2.isPending}
                        title={tituloAcaoLink(
                          'Copiar Link da Etapa 2',
                          'Link da Etapa 2 copiado',
                          candidato.linkEtapa2CopiadoEm,
                          candidato.linkEtapa2CopiadoPor
                        )}
                      >
                        <LinkIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        className={
                          candidato.linkEtapa2EnviadoEm
                            ? 'text-blue-500 hover:text-blue-600'
                            : 'text-gray-400 hover:text-gray-600'
                        }
                        onClick={() => handleEnviarLinkEtapa2(candidato)}
                        disabled={enviarLinkEtapa2.isPending}
                        title={tituloAcaoLink(
                          'Enviar e-mail da Etapa 2',
                          'E-mail da Etapa 2 enviado',
                          candidato.linkEtapa2EnviadoEm,
                          candidato.linkEtapa2EnviadoPor
                        )}
                      >
                        <EnvelopeIcon className="h-4 w-4" />
                      </button>
                    </>
                  )}
                  {isRecrutamentoAdmin && (
                    <>
                      <button
                        type="button"
                        className="text-gray-400 hover:text-gray-600"
                        onClick={() => setCandidatoEditando(candidato)}
                        title="Editar candidato"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        className="text-gray-400 hover:text-red-600"
                        onClick={() => handleExcluir(candidato)}
                        title="Excluir candidato"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <AdicionarCandidatoModal isOpen={modalAberto} onClose={() => setModalAberto(false)} />
      <EditarCandidatoModal candidato={candidatoEditando} onClose={() => setCandidatoEditando(null)} />
      <VisualizarCandidatoModal candidato={candidatoVisualizando} onClose={() => setCandidatoVisualizando(null)} />
      <ConfirmDialog
        isOpen={confirmacao != null}
        title={confirmacao ? TITULO_CONFIRMACAO[confirmacao.tipo] : ''}
        message={confirmacao ? mensagemConfirmacao(confirmacao) : ''}
        type={confirmacao?.tipo === 'excluir' ? 'danger' : 'info'}
        confirmText={confirmacao?.tipo === 'excluir' ? 'Excluir' : 'Enviar'}
        onCancel={() => setConfirmacao(null)}
        onConfirm={handleConfirmar}
      />
    </div>
  );
}
